import { FlightIngredient } from '@apps/main/modules/recipe/ingredients';

import type {
  ExposedFlightIngredient,
  OldExposedFlightIngredient,
  ExposedTransportIngredient,
} from '@libs/trip-planner-interfaces';
import { IngredientType } from '@libs/trip-planner-interfaces';
import { assertValue } from '@libs/utils';
import { toAvailabilityOrder } from '@libs/search-service-smart-search';

import { isAvailabilityOrderQl } from './recipeSerializer.helpers';

export function isOldExposedFlightIngredient(
  exposedFlightIngredient: ExposedFlightIngredient | OldExposedFlightIngredient
): exposedFlightIngredient is OldExposedFlightIngredient {
  return 'from' in exposedFlightIngredient || 'to' in exposedFlightIngredient;
}

export function createExposedFlightIngredient(
  flightIngredient: FlightIngredient
): ExposedFlightIngredient {
  const { fromPort, toPort, transportIds, filters } = flightIngredient;

  assertValue(
    fromPort,
    'recipeSerializer.flight.helpers > createExposedFerryIngredient > fromPort'
  );
  assertValue(
    toPort,
    'recipeSerializer.flight.helpers > createExposedFerryIngredient > toPort'
  );

  return {
    ingredientType: IngredientType.Flight,
    fromIata: fromPort,
    toIata: toPort,
    flightNumbers: transportIds,
    maxNumberOfStops: filters.maxNumberOfStops,
    departureTimeFilters: filters.departureTimeFilters,
    arrivalTimeFilters: filters.arrivalTimeFilters,
    maxDurationInHours: filters.maxDurationInHours,
    priceFilters: filters.priceFilters,
    carriers: filters.carrierFilters,
  };
}

export function createFlightIngredient(
  exposedFlightIngredient:
    | ExposedFlightIngredient
    | OldExposedFlightIngredient
    | ExposedTransportIngredient
): FlightIngredient {
  const {
    fromIata,
    toIata,
    flightNumbers,
    maxNumberOfStops,
    departureTimeFilters,
    arrivalTimeFilters,
    maxDurationInHours,
    priceFilters,
    carriers,
    suggestedBy,
  } = exposedFlightIngredient as
    ExposedFlightIngredient | ExposedTransportIngredient;

  // Backwards Compatibility
  const { from, to } = exposedFlightIngredient as OldExposedFlightIngredient;

  const flightIngredient = new FlightIngredient();
  flightIngredient.fromPort = fromIata ?? from?.iata;
  flightIngredient.toPort = toIata ?? to?.iata;
  flightIngredient.transportIds = flightNumbers;
  flightIngredient.filters.maxNumberOfStops = maxNumberOfStops;
  flightIngredient.filters.departureTimeFilters = departureTimeFilters;
  flightIngredient.filters.arrivalTimeFilters = arrivalTimeFilters;
  flightIngredient.filters.maxDurationInHours = maxDurationInHours;
  flightIngredient.filters.priceFilters = priceFilters;
  flightIngredient.filters.carrierFilters = carriers ?? [];

  if (suggestedBy) {
    flightIngredient.availabilityOrder = isAvailabilityOrderQl(suggestedBy)
      ? toAvailabilityOrder(suggestedBy)
      : suggestedBy;
  }

  return flightIngredient;
}
